/**
 * Jukebox — Product Module
 *
 * Drives sections/jukebox-product-module.liquid:
 *   - gallery thumb switching
 *   - Deodorant / Soap tabs in "Explore more scents"
 *   - one selection model across the green banner AND both scent tabs: Add sets a
 *     quantity of 1, the stepper adjusts it, dropping to 0 deselects
 *   - the running total on the purchase options and the Add to cart button
 *   - Subscribe & Save redirects; One time purchase posts every selected product as
 *     its OWN cart line in a single request — separate lines, not a bundle
 *   - drag-to-scroll on the scent panels and the pairs row
 *
 * There is no main product: the total is purely the sum of what the shopper selects,
 * and nothing is selected by default. "Pairs well with" is deliberately outside this
 * model — those cards add straight to the cart via the theme's product-form-component,
 * so they carry no data-jpm-item.
 */

/*
 * Money formatting is intentionally self-contained.
 *
 * This used to be `import { formatMoney } from '@theme/money-formatting'` — a bare
 * specifier resolved by the importmap in snippets/scripts.liquid. If that mapping is
 * unavailable for any reason, the import fails and the WHOLE module never executes: no
 * tabs, no Add buttons, no total on the CTA, and nothing obvious in the console. A single
 * shared dependency taking the entire section down is not worth the reuse, so the few
 * lines needed are inlined here.
 *
 * Replicates Shopify's `money` filter for the placeholders it actually emits.
 */
const CURRENCY_DECIMALS = { BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0, RWF: 0, UGX: 0, VND: 0, XAF: 0, XOF: 0, BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3 };

/**
 * @param {number} cents
 * @param {string} format Shopify money format, e.g. '${{amount}}'
 * @param {string} currency ISO code
 */
function formatMoney(cents, format, currency) {
  const precision = CURRENCY_DECIMALS[String(currency).toUpperCase()] ?? 2;

  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    if (token === 'currency') return currency;

    let thousands = ',';
    let decimal = '.';
    let digits = precision;

    switch (token) {
      case 'amount_no_decimals':
        digits = 0;
        break;
      case 'amount_with_comma_separator':
        thousands = '.';
        decimal = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        thousands = '.';
        digits = 0;
        break;
      case 'amount_with_space_separator':
        thousands = ' ';
        decimal = ',';
        break;
      case 'amount_no_decimals_with_space_separator':
        thousands = ' ';
        digits = 0;
        break;
      case 'amount_with_period_and_space_separator':
        thousands = ' ';
        break;
      case 'amount_with_apostrophe_separator':
        thousands = "'";
        break;
      default:
        // 'amount' and anything unrecognised keep the currency defaults.
        break;
    }

    const value = (Math.round(cents) / Math.pow(10, precision)).toFixed(digits);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return digits > 0 && fraction ? grouped + decimal + fraction : grouped;
  });
}

const NBSP = ' ';
const SEPARATOR = `${NBSP}${NBSP}·${NBSP}${NBSP}`;

/** Movement in px before a pointer gesture counts as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

/** Controls inside a drag-scroll row: a press here is a click, never a drag. */
const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"]';

/**
 * Click-and-drag horizontal scrolling for a native overflow container.
 *
 * These rows are `overflow-x: auto` with the scrollbar hidden, so a mouse has no way to
 * move them — touch and trackpads scroll natively, a mouse does not. Mouse and pen only;
 * touch is left to the browser, which does it better.
 *
 * @param {HTMLElement} el
 */
function enableDragScroll(el) {
  if (!el || el.dataset.dragScroll === 'true') return;
  el.dataset.dragScroll = 'true';

  let pointerId = null;
  let startX = 0;
  let startScrollLeft = 0;
  let travelled = 0;

  /** @param {Event} event */
  const swallowClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const syncScrollable = () => {
    el.toggleAttribute('data-scrollable', el.scrollWidth > el.clientWidth + 1);
  };

  el.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch' || event.button !== 0) return;
    // Never start a drag from a control. The stepper's +/- are 40px targets, so an
    // ordinary click drifts a few px; that used to cross the drag threshold, take pointer
    // capture and swallow the click, which made the buttons feel dead. Grab the row
    // anywhere else to scroll it.
    if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startScrollLeft = el.scrollLeft;
    travelled = 0;
  });

  el.addEventListener('pointermove', (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    const dx = event.clientX - startX;
    travelled = Math.max(travelled, Math.abs(dx));
    if (travelled <= DRAG_THRESHOLD) return;

    // Guarded: setPointerCapture throws if the pointer is no longer active, and that
    // would abort the handler before the scroll below.
    if (!el.hasPointerCapture(pointerId)) {
      try {
        el.setPointerCapture(pointerId);
      } catch {
        /* Dragging still works, it just stops at the element's edge. */
      }
    }
    el.classList.add('is-dragging');
    el.scrollLeft = startScrollLeft - dx;
    event.preventDefault();
  });

  const endDrag = () => {
    if (pointerId === null) return;
    if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
    pointerId = null;
    el.classList.remove('is-dragging');

    if (travelled > DRAG_THRESHOLD) {
      // Swallow the click that follows a drag so the Add buttons don't fire.
      el.addEventListener('click', swallowClick, { capture: true, once: true });
      setTimeout(() => el.removeEventListener('click', swallowClick, { capture: true }), 0);
    }
  };

  el.addEventListener('pointerup', endDrag);
  el.addEventListener('pointercancel', endDrag);
  el.addEventListener('dragstart', (event) => event.preventDefault());

  syncScrollable();
  if ('ResizeObserver' in window) new ResizeObserver(syncScrollable).observe(el);
}

class JukeboxProductModule {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.currency = root.dataset.currency || 'USD';
    this.discount = parseFloat(root.dataset.discount || '0') || 0;
    this.subscribeUrl = root.dataset.subscribeUrl || '';
    this.ctaText = root.dataset.ctaText || 'ADD TO CART';
    this.subscribeCtaText = root.dataset.subscribeCtaText || 'SUBSCRIBE';
    this.submitting = false;

    this.cta = root.querySelector('[data-jpm-cta]');
    this.ctaLabel = root.querySelector('[data-jpm-cta-label]');
    this.errorEl = root.querySelector('[data-jpm-error]');

    /** @type {Map<number, { quantity: number, price: number, el: HTMLElement }>} */
    this.selection = new Map();

    this.#bindGallery();
    this.#bindTabs();
    this.#bindItems();
    this.#bindPlan();
    this.#bindCta();
    this.#bindDragScroll();
    this.render();
  }

  // -- money ---------------------------------------------------------------

  /** @param {number} cents */
  money(cents) {
    return formatMoney(Math.round(cents), this.moneyFormat, this.currency);
  }

  /** Sum of every selected product across the green banner and both scent tabs. */
  total() {
    let sum = 0;
    for (const entry of this.selection.values()) sum += entry.price * entry.quantity;
    return sum;
  }

  subscribeTotal() {
    return (this.total() * (100 - this.discount)) / 100;
  }

  get isSubscribe() {
    const checked = this.root.querySelector('[data-jpm-plan]:checked');
    return !checked || checked.value === 'subscribe';
  }

  // -- gallery -------------------------------------------------------------

  #bindGallery() {
    const main = this.root.querySelector('[data-jpm-main-image]');
    this.thumbs = Array.from(this.root.querySelectorAll('[data-jpm-thumb]'));
    if (!main || !this.thumbs.length) return;

    const select = (index) => {
      const thumb = this.thumbs[index];
      if (!thumb) return;
      this.thumbIndex = index;
      main.removeAttribute('srcset');
      main.src = thumb.dataset.image;
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
    };

    this.thumbIndex = 0;
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => select(index)));

    // Both arrows wrap around, so the gallery never dead-ends on the first or last image.
    const step = (delta) => {
      const count = this.thumbs.length;
      select((this.thumbIndex + delta + count) % count);
    };

    const next = this.root.querySelector('[data-jpm-next]');
    if (next) next.addEventListener('click', () => step(1));

    const prev = this.root.querySelector('[data-jpm-prev]');
    if (prev) prev.addEventListener('click', () => step(-1));
  }

  // -- Deodorant / Soap tabs -----------------------------------------------

  /**
   * Panels are hidden rather than unmounted, so a selection made on one tab survives
   * switching to the other and still counts toward the total.
   */
  #bindTabs() {
    const tabs = Array.from(this.root.querySelectorAll('[data-jpm-tab]'));
    if (tabs.length < 2) return;

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const key = tab.dataset.jpmTab;
        for (const other of tabs) {
          const active = other === tab;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-selected', active ? 'true' : 'false');
        }
        for (const panel of this.root.querySelectorAll('[data-jpm-tabpanel]')) {
          panel.toggleAttribute('hidden', panel.dataset.jpmTabpanel !== key);
        }
      });
    }
  }

  // -- selection -----------------------------------------------------------

  #bindItems() {
    this.items = Array.from(this.root.querySelectorAll('[data-jpm-item]'));

    for (const el of this.items) {
      const variantId = parseInt(el.dataset.variantId || '', 10);
      if (!variantId) continue;
      const price = parseInt(el.dataset.price || '0', 10) || 0;

      // Cards marked data-jpm-default-qty start selected — the green banner pair does, so
      // the Add to cart total covers both on load. Everything else defaults to 0.
      const defaultQty = parseInt(el.dataset.jpmDefaultQty || '0', 10) || 0;
      if (defaultQty > 0) {
        this.selection.set(variantId, { quantity: defaultQty, price, el });
      }

      const selectBtn = el.querySelector('[data-jpm-select]');
      const input = el.querySelector('[data-jpm-qty]');

      if (selectBtn) {
        selectBtn.addEventListener('click', () => {
          this.#setQuantity(variantId, price, el, 1);
          const field = el.querySelector('[data-jpm-qty]');
          if (field) field.focus();
        });
      }

      for (const btn of el.querySelectorAll('[data-jpm-step]')) {
        btn.addEventListener('click', () => {
          const entry = this.selection.get(variantId);
          const current = entry ? entry.quantity : 0;
          this.#setQuantity(variantId, price, el, current + parseInt(btn.dataset.jpmStep, 10));
        });
      }

      if (input) {
        input.addEventListener('change', () => {
          const next = parseInt(input.value, 10);
          this.#setQuantity(variantId, price, el, Number.isNaN(next) ? 0 : next);
        });
      }

      this.#syncItem(el);
    }
  }

  /** Dropping to 0 (or below) deselects the product. */
  #setQuantity(variantId, price, el, quantity) {
    if (quantity <= 0) {
      this.selection.delete(variantId);
    } else {
      this.selection.set(variantId, { quantity, price, el });
    }
    this.#syncItem(el);
    this.render();
  }

  /** Mirror the selection state onto one card. */
  #syncItem(el) {
    const variantId = parseInt(el.dataset.variantId || '', 10);
    const entry = this.selection.get(variantId);
    const selectBtn = el.querySelector('[data-jpm-select]');
    const stepper = el.querySelector('[data-jpm-stepper]');
    const input = el.querySelector('[data-jpm-qty]');

    if (selectBtn) selectBtn.hidden = Boolean(entry);
    if (stepper) stepper.hidden = !entry;
    if (input) input.value = String(entry ? entry.quantity : 0);
  }

  /**
   * Back to the default state after a successful add: cards with a default quantity (the
   * green banner pair) return to it rather than emptying out, so the CTA total is never
   * left at zero.
   */
  #resetSelection() {
    const touched = Array.from(this.selection.values()).map((entry) => entry.el);
    this.selection.clear();

    for (const el of this.items || []) {
      const variantId = parseInt(el.dataset.variantId || '', 10);
      const defaultQty = parseInt(el.dataset.jpmDefaultQty || '0', 10) || 0;
      if (!variantId || defaultQty <= 0) continue;
      this.selection.set(variantId, {
        quantity: defaultQty,
        price: parseInt(el.dataset.price || '0', 10) || 0,
        el,
      });
    }

    const all = new Set([...touched, ...(this.items || [])]);
    for (const el of all) this.#syncItem(el);
  }

  // -- purchase options ----------------------------------------------------

  #bindPlan() {
    for (const radio of this.root.querySelectorAll('[data-jpm-plan]')) {
      radio.addEventListener('change', () => this.render());
    }
  }

  // -- render --------------------------------------------------------------

  render() {
    const total = this.total();
    const subTotal = this.subscribeTotal();

    // Nothing selected means there is no price to show — hide the amounts rather than
    // printing $0.00, and drop the price off the CTA so it reads as a plain label.
    const hasPrice = total > 0;
    // Subscribe & Save hands the shopper off to the subscription flow, which sets its own
    // pricing — so while that option is selected no amount is shown anywhere.
    const showPrice = hasPrice && !this.isSubscribe;

    const onetime = this.root.querySelector('[data-jpm-onetime-price]');
    if (onetime) {
      onetime.textContent = this.money(total);
      onetime.hidden = !showPrice;
    }

    // The Subscribe & Save row never shows an amount at all — the subscription flow sets its
    // own pricing — so [data-jpm-sub-prices] stays hidden from the markup and is only kept
    // up to date here in case it is ever shown again.

    const was = this.root.querySelector('[data-jpm-sub-was]');
    if (was) {
      was.textContent = this.money(total);
    }

    const now = this.root.querySelector('[data-jpm-sub-now]');
    if (now) {
      now.textContent = this.money(subTotal);
    }

    if (this.ctaLabel) {
      const label = this.isSubscribe ? this.subscribeCtaText : this.ctaText;
      this.ctaLabel.textContent = showPrice ? `${label}${SEPARATOR}${this.money(total)}` : label;
    }

    if (this.cta) {
      // Subscribe needs a destination; One time purchase needs something selected.
      const blocked = this.isSubscribe
        ? !this.subscribeUrl || this.submitting
        : this.selection.size === 0 || this.submitting;
      this.cta.toggleAttribute('disabled', blocked);
      this.cta.setAttribute('aria-busy', this.submitting ? 'true' : 'false');
    }
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #hideError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  // -- cart ----------------------------------------------------------------

  #bindCta() {
    if (!this.cta) return;
    this.cta.addEventListener('click', () => {
      if (this.isSubscribe) {
        if (this.subscribeUrl) window.location.href = this.subscribeUrl;
        return;
      }
      this.#addToCart();
    });
  }

  async #addToCart() {
    if (this.submitting || this.selection.size === 0) return;

    this.#hideError();
    this.submitting = true;
    this.render();

    // One line item per selected product — deliberately not a bundle.
    const items = [];
    for (const [variantId, entry] of this.selection) {
      items.push({ id: variantId, quantity: entry.quantity });
    }

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    try {
      const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#showError(data.description || data.message || 'Could not add to cart.');
        return;
      }

      this.#resetSelection();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'jukebox-product-module',
            data: {
              source: 'product-form-component',
              sections: data.sections,
              itemCount: items.length,
            },
          },
        })
      );

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (error) {
      console.error('[jukebox-product-module] add to cart failed', error);
      this.#showError('Could not add to cart. Please try again.');
    } finally {
      this.submitting = false;
      this.render();
    }
  }

  // -- drag-to-scroll ------------------------------------------------------

  #bindDragScroll() {
    for (const el of this.root.querySelectorAll('[data-jpm-tabpanel], .jpm__pairs-row')) {
      enableDragScroll(el);
    }
  }
}

function init() {
  for (const root of document.querySelectorAll('[data-jpm]')) {
    if (root.dataset.jpmReady === 'true') continue;
    root.dataset.jpmReady = 'true';
    new JukeboxProductModule(root);
  }
}

init();

// Theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', init);
